import { all, call, delay, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { apiFailureMessage } from 'library/helpers/apiFailureMessage';
import SuperFetch from 'library/helpers/superFetch';
import { selectRoomStaysEnabled } from 'reduxStore/roomStays/selectors';
import { newOperationKey } from 'reduxStore/roomStays/saga';
import { cardStockActions } from './slice';
import { canonicalEnrolCards, selectDraft, selectEnrolPlan } from './selectors';

/**
 * Room cards setup — Card Stock tab requests (PLAN.md v0.7.9 §4.1, work row 10). Every route is under
 * `/v5/:orgID/room-stays/stock/:siteID`, and none is called while `credentials.roomStays` is off (INV-01).
 */

const OK = new Set([200, 201, 202]);
// Classify is re-run as cards arrive; a burst of scans becomes one request.
export const CLASSIFY_DEBOUNCE_MS = 400;
export const PROGRESS_POLL_MS = 5000;
export const PROGRESS_POLL_MAX = 120;
/**
 * Outcomes that will not change. `failed` is terminal too: the server sets it only after its last attempt; a card it
 * is still retrying reads outcome null.
 */
const SETTLED_OUTCOMES = new Set(['created', 'moved', 'already_in_stock', 'refused', 'failed']);
/** Operation `hardwareState`s that do not resolve on their own: `blocked` waits on a repair, not on time. */
const SETTLED_HARDWARE = new Set(['complete', 'not_required', 'blocked']);
/** Outcomes that landed the card in Card Stock. */
const LANDED_OUTCOMES = new Set(['created', 'moved', 'already_in_stock']);

const stockPath = (siteID) => `/room-stays/stock/${siteID}`;

/** A refusal as the room-stays router sends it: `{ info: message, data: { reason: code } }`. */
const refusalOf = (res, fallback) => ({
	code: res?.data?.reason || null,
	message: apiFailureMessage(res, fallback)
});

/**
 * A repair refusal. Repair answers 503 "Delivery state could not be read; try again." on purpose, and that sentence is
 * the actionable one; `apiFailureMessage` hides every 5xx, so a 503's own `info` is shown. Any other 5xx stays hidden.
 */
const repairRefusalOf = (res) =>
	res?.statusCode === 503 && typeof res.info === 'string' && res.info.trim()
		? { code: null, message: res.info.trim() }
		: refusalOf(res, 'Could not repair this card.');

/**
 * Whether polling has nothing left to wait for: every card has a settled outcome, and the operation's
 * `hardwareState` is not `pending` (`blocked` waits on a repair, so it is not polled). Without a `hardwareState`, no
 * card may still be "Waiting for locks".
 *
 * Checked before the `hardwareState` gate: when every card that landed has moved since (`deliveryHolder: 'current'`),
 * any lock updating left is for those cards' new holders, so nothing in this enrolment is still to come.
 */
export const operationSettled = (operation) => {
	const cards = operation?.cards || [];
	if (!cards.length || !cards.every((c) => SETTLED_OUTCOMES.has(c.outcome))) return false;
	const landed = cards.filter((c) => LANDED_OUTCOMES.has(c.outcome));
	if (landed.length && landed.every((c) => c.deliveryHolder === 'current')) return true;
	if (operation.hardwareState != null) return SETTLED_HARDWARE.has(operation.hardwareState);
	// A card that has moved since (`deliveryHolder: 'current'`) updates for its new holder, not for this enrolment.
	return !cards.some((c) => c.state === 'locks_updating' && c.deliveryHolder !== 'current');
};

function* flagOn() {
	return Boolean(yield select(selectRoomStaysEnabled));
}

// POST stock/:siteID/enrol/classify — read-only; every card on the draft, the result merged back by CSN.
export function* classifyCards({ payload: { siteID } }) {
	try {
		yield delay(CLASSIFY_DEBOUNCE_MS);
		if (!(yield* flagOn())) return;
		const draft = yield select(selectDraft, siteID);
		// `{ csn }` only: classifyEnrolSchema refuses any other key (400). Names travel on enrol.
		const cards = (draft?.rows || []).filter((r) => r.csn).map((r) => ({ csn: r.csn }));
		if (!cards.length) {
			yield put(cardStockActions.classifyCardsSuccess({ siteID, cards: [] }));
			return;
		}
		const res = yield call(() => SuperFetch.post(`${stockPath(siteID)}/enrol/classify`, { cards }));
		if (OK.has(res?.statusCode)) yield put(cardStockActions.classifyCardsSuccess({ siteID, cards: res.data?.cards || [] }));
		else yield put(cardStockActions.classifyCardsError({ siteID, error: refusalOf(res, 'Could not check these cards.') }));
	} catch (error) {
		yield put(cardStockActions.classifyCardsError({ siteID, error: { code: null, message: 'Could not check these cards.' } }));
	}
}

/**
 * POST stock/:siteID/enrol — one `enrolKey` per submitted list (§5.2). The key is kept on the draft with the cards it
 * was made for, and stored before the request leaves: the same cards resent reuse it (the server replays), changed
 * cards get a new one.
 */
export function* enrolCards({ payload: { siteID } }) {
	const draft = yield select(selectDraft, siteID);
	// Each submission has its own identity; its answer is applied only while the draft still carries it.
	let submissionID = draft?.submissionID ?? null;
	const fail = (error) => put(cardStockActions.enrolCardsError({ siteID, submissionID, error }));
	try {
		if (!(yield* flagOn())) {
			yield fail({ code: null, message: 'Front Desk is not on.' });
			return;
		}
		const { cards } = yield select(selectEnrolPlan, siteID);
		if (!cards.length) {
			yield fail({ code: null, message: 'No cards are ready to add.' });
			return;
		}
		const enrolKeyFor = canonicalEnrolCards(cards);
		const enrolKey = draft?.enrolKey && draft.enrolKeyFor === enrolKeyFor ? draft.enrolKey : newOperationKey();
		submissionID = newOperationKey();
		yield put(cardStockActions.setEnrolKey({ siteID, enrolKey, enrolKeyFor, submissionID }));
		const res = yield call(() => SuperFetch.post(`${stockPath(siteID)}/enrol`, { enrolKey, cards }));
		if (OK.has(res?.statusCode) && res.data?.operationID != null) {
			yield put(cardStockActions.enrolCardsSuccess({ siteID, submissionID, operation: res.data }));
			// Polled only if the answer was taken: a stale one (the draft was reset meanwhile) starts nothing.
			if ((yield select(selectDraft, siteID))?.submissionID === submissionID)
				yield put(cardStockActions.startProgress({ siteID, operationID: res.data.operationID }));
		} else yield fail(refusalOf(res, 'Could not add these cards.'));
	} catch (error) {
		yield fail({ code: null, message: 'Could not add these cards.' });
	}
}

// GET stock/:siteID/enrol/:operationID, re-read until the operation settles, the tab stops it, or the cap is reached.
export function* pollProgress(siteID, operationID) {
	for (let i = 0; i < PROGRESS_POLL_MAX; i += 1) {
		yield delay(PROGRESS_POLL_MS);
		if (!(yield* flagOn())) return;
		const res = yield call(() => SuperFetch.get(`${stockPath(siteID)}/enrol/${operationID}`));
		if (OK.has(res?.statusCode) && res.data) {
			yield put(cardStockActions.loadProgressSuccess({ siteID, operation: res.data }));
			if (operationSettled(res.data)) {
				yield put(cardStockActions.loadStockCards({ siteID }));
				return;
			}
		} else yield put(cardStockActions.loadProgressError({ siteID, error: refusalOf(res, 'Could not read progress.') }));
	}
	// Still working after the cap: say so, rather than leave a list that silently stopped moving.
	yield put(cardStockActions.progressPollExhausted({ siteID }));
}

export function* watchProgress({ payload: { siteID, operationID } }) {
	try {
		yield race({
			poll: call(pollProgress, siteID, operationID),
			stop: take((a) =>
				(a.type === cardStockActions.stopProgress.type || a.type === cardStockActions.clearDraft.type) &&
				String(a.payload?.siteID) === String(siteID)
			)
		});
	} catch (error) {
		yield put(cardStockActions.loadProgressError({ siteID, error: { code: null, message: 'Could not read progress.' } }));
	}
}

// GET stock/:siteID/cards — Card Stock and Card Store cards with the server's delivery state.
export function* loadStockCards({ payload: { siteID } }) {
	try {
		if (!(yield* flagOn())) {
			yield put(cardStockActions.loadStockCardsSuccess({ siteID, data: null }));
			return;
		}
		const res = yield call(() => SuperFetch.get(`${stockPath(siteID)}/cards`));
		if (OK.has(res?.statusCode)) yield put(cardStockActions.loadStockCardsSuccess({ siteID, data: res.data || null }));
		else yield put(cardStockActions.loadStockCardsError({ siteID, error: refusalOf(res, 'Could not load the cards.') }));
	} catch (error) {
		yield put(cardStockActions.loadStockCardsError({ siteID, error: { code: null, message: 'Could not load the cards.' } }));
	}
}

// POST stock/:siteID/cards/:credentialID/repair — that one card on that site, then the list is read again.
export function* repairCard({ payload: { siteID, credentialID } }) {
	try {
		if (!(yield* flagOn())) return;
		const res = yield call(() => SuperFetch.post(`${stockPath(siteID)}/cards/${credentialID}/repair`, {}));
		if (OK.has(res?.statusCode)) {
			yield put(cardStockActions.repairCardSuccess({ siteID, credentialID, result: res.data || {} }));
			yield put(cardStockActions.loadStockCards({ siteID }));
		} else
			yield put(
				cardStockActions.repairCardError({ siteID, credentialID, error: repairRefusalOf(res) })
			);
	} catch (error) {
		yield put(
			cardStockActions.repairCardError({
				siteID,
				credentialID,
				error: { code: null, message: 'Could not repair this card.' }
			})
		);
	}
}

export default function* rootSaga() {
	yield all([
		takeLatest(cardStockActions.classifyCards.type, classifyCards),
		takeLatest(cardStockActions.enrolCards.type, enrolCards),
		takeLatest(cardStockActions.startProgress.type, watchProgress),
		takeLatest(cardStockActions.loadStockCards.type, loadStockCards),
		takeEvery(cardStockActions.repairCard.type, repairCard)
	]);
}
